import { PropsWithChildren, ReactElement } from 'react';
import styled from 'styled-components';

import { Box, Grid } from '@breathelife/mui';

import { PdfFieldGroupHeader, PdfQuestionnaireSectionHeader, PdfSubsectionHeader } from '@breathelife/ui-components';

import usePdfContext from '../../../../Context/usePdfContext';
import { translate } from '../../../../Localization/Localizer';
import {
  ComprehensivePricingCosts,
  ComprehensivePricingCurrency,
  ComprehensivePricingPartyRole,
  ComprehensivePricingProduct,
  ComprehensivePricingRider,
  ComprehensivePricingSupportedPaymentModes,
  Language,
} from '@breathelife/types';
import { toCurrency } from '../../../../helpers/currency';
import { QuestionAnswerGroupA } from './Styles/PdfQuestionAnswerGroupA';
import { IconNames } from '@zinnia/form-engine-sdk';

const paymentModeToCostKeyMap: Record<ComprehensivePricingSupportedPaymentModes, keyof ComprehensivePricingCosts> = {
  [ComprehensivePricingSupportedPaymentModes.annual]: 'annualCost',
  [ComprehensivePricingSupportedPaymentModes.quarterly]: 'quarterlyCost',
  [ComprehensivePricingSupportedPaymentModes.monthly]: 'monthlyCost',
  [ComprehensivePricingSupportedPaymentModes.semiAnnual]: 'semiAnnualCost',
  [ComprehensivePricingSupportedPaymentModes.singlePremium]: 'singlePremiumCost',
};

const paymentModeToPremiumTextKeyMap: Partial<Record<ComprehensivePricingSupportedPaymentModes, string>> = {
  [ComprehensivePricingSupportedPaymentModes.annual]: 'annualPremium',
  [ComprehensivePricingSupportedPaymentModes.quarterly]: 'quarterlyPremium',
  [ComprehensivePricingSupportedPaymentModes.monthly]: 'monthlyPremium',
  [ComprehensivePricingSupportedPaymentModes.semiAnnual]: 'semiAnnualPremium',
};

export function paymentModeToModeToCost(
  paymentMode: ComprehensivePricingSupportedPaymentModes,
  comprehensivePricingCosts: ComprehensivePricingCosts,
  currency: ComprehensivePricingCurrency,
): number | undefined {
  return comprehensivePricingCosts[paymentModeToCostKeyMap[paymentMode]]?.[currency];
}

export function formatPartyRole(roles: ComprehensivePricingPartyRole[]): string {
  const displayNames = roles.map((role) => {
    const roleDisplayName = translate(`comprehensivePricing.partyRoles.${role.roleCode}`);
    return roleDisplayName + (role.roleIndex ? ` ${role.roleIndex}` : '');
  });
  return `${displayNames.join(', ')}`;
}

const ProductContainer = styled.div`
  & {
    break-inside: avoid;
  }
`;

type TotalProps = {
  displayName: string;
  cost?: number;
  currency: ComprehensivePricingCurrency;
  language: Language;
  paymentMode: ComprehensivePricingSupportedPaymentModes;
  type: 'total' | 'subtotal';
};
export function Total(props: TotalProps): ReactElement {
  const Element = props.type === 'total' ? PdfQuestionnaireSectionHeader : PdfSubsectionHeader;

  return (
    <Grid container>
      <Grid item xs={9}>
        <Element>{props.displayName}</Element>
      </Grid>
      <Grid item xs={3}>
        {(props.cost || props.cost === 0) && (
          <Element>
            {translate(`comprehensivePricing.totalCost.${props.paymentMode}`, {
              cost: toCurrency(props.cost || 0, props.currency, props.language),
            })}
          </Element>
        )}
      </Grid>
    </Grid>
  );
}

type ComprehensivePricingWidgetProductCardProps = {
  displayName: string;
  type: 'product' | 'rider' | 'esa';
  cost?: number;
  isJointProduct?: boolean;
  coverageAmount?: number;
  currency?: ComprehensivePricingCurrency;
  isDuplicateCost?: boolean;
  isFirstInList?: boolean;
  tooltipMessage?: string;
  equivalentSingleAge?: number;
  language: Language;
  paymentMode: ComprehensivePricingSupportedPaymentModes;
};
const productCardTypeToFirstColumnDescriptionMap: Partial<
  Record<ComprehensivePricingWidgetProductCardProps['type'], string>
> = {
  product: 'productDescription',
  rider: 'riderDescription',
};

export function ComprehensivePricingProductCard(
  props: PropsWithChildren<ComprehensivePricingWidgetProductCardProps>,
): ReactElement {
  const displayedCost =
    props.isDuplicateCost || !props.currency ? '-' : toCurrency(props.cost || 0, props.currency, props.language);
  const firstColumnLabel = productCardTypeToFirstColumnDescriptionMap[props.type];
  return (
    <Box>
      <Grid container>
        <Grid item xs={props.type !== 'esa' && props.currency && props.coverageAmount ? 6 : 9}>
          <QuestionAnswerGroupA
            valueContainerLeftIndent={props.type === 'rider' ? 2 : 0}
            alignLabelWithValue={true}
            value={props.displayName}
            valueWeight={400}
            labelText={
              firstColumnLabel
                ? props.isFirstInList
                  ? translate(`comprehensivePricing.${firstColumnLabel}`)
                  : undefined
                : undefined
            }
            valueRightIconName={props.isJointProduct ? IconNames.group : undefined}
          />
        </Grid>
        {props.type !== 'esa' && props.currency && props.coverageAmount && (
          <Grid item xs={3}>
            <QuestionAnswerGroupA
              alignLabelWithValue={true}
              value={toCurrency(props.coverageAmount, props.currency, props.language)}
              labelText={props.isFirstInList ? translate('comprehensivePricing.coverage') : undefined}
            />
          </Grid>
        )}

        <Grid item xs={3}>
          {props.equivalentSingleAge && props.type === 'esa' ? (
            <QuestionAnswerGroupA
              alignLabelWithValue={true}
              value={translate('comprehensivePricing.equivalentSingleAge.value', {
                age: props.equivalentSingleAge.toString(),
              })}
            />
          ) : (
            <QuestionAnswerGroupA
              value={props.cost || props.cost === 0 ? displayedCost : ''}
              valueWeight={400}
              alignLabelWithValue={true}
              labelText={
                props.isFirstInList
                  ? translate(`comprehensivePricing.${paymentModeToPremiumTextKeyMap[props.paymentMode]}`)
                  : undefined
              }
            />
          )}
        </Grid>
      </Grid>

      {props.children && <div>{props.children}</div>}
    </Box>
  );
}

export function ComprehensivePricing(): ReactElement | null {
  const { comprehensivePricing, application } = usePdfContext();
  const { lang: language } = application;

  const paymentMode = comprehensivePricing?.supportedPaymentModes[0];

  if (typeof comprehensivePricing === 'undefined' || !paymentMode) {
    return null;
  }
  // What should the title be?
  return (
    <Box mb={3}>
      <Grid container>
        <Grid item xs={9}>
          <ProductContainer>
            <PdfFieldGroupHeader data-testid='comprehensivePricing'>
              {translate('comprehensivePricing.title', { locale: language, count: 2 })}
            </PdfFieldGroupHeader>
            {comprehensivePricing.contracts.map((contract, contractIndex) => {
              const showContractSection =
                contract.riders.length || (contract.fees && !contract.fees.hideFees) || contract.equivalentSingleAge;
              const hasMultipleContracts = comprehensivePricing.contracts.length > 1;
              return (
                <Box key={contractIndex} ml={1} mb={2}>
                  {hasMultipleContracts && (
                    <Box mb={1}>
                      <PdfSubsectionHeader>
                        {translate(`comprehensivePricing.contractTitle.${contractIndex + 1}`)}
                      </PdfSubsectionHeader>
                    </Box>
                  )}

                  <Box ml={hasMultipleContracts ? 1 : 0}>
                    {contract.parties.map((party) => {
                      return (
                        <Box key={`${party.firstName} ${party.lastName}`} mb={1}>
                          <Box mb={0.5}>
                            <PdfSubsectionHeader>
                              {`${party.firstName} ${party.lastName} (${formatPartyRole(party.roles)})`}
                            </PdfSubsectionHeader>
                          </Box>
                          {(party.products.length || party.riders.length) && (
                            <div>
                              {party.products.map((product: ComprehensivePricingProduct, index) => (
                                <ComprehensivePricingProductCard
                                  paymentMode={paymentMode}
                                  key={product.productCode}
                                  language={language}
                                  displayName={product.displayName?.[language] || product.productCode}
                                  cost={paymentModeToModeToCost(
                                    paymentMode,
                                    {
                                      monthlyCost: product.monthlyCost,
                                      annualCost: product.annualCost,
                                      quarterlyCost: product.quarterlyCost,
                                      semiAnnualCost: product.semiAnnualCost,
                                      singlePremiumCost: product.singlePremiumCost,
                                    },
                                    ComprehensivePricingCurrency.CAD,
                                  )}
                                  isDuplicateCost={product.isDuplicateCost}
                                  type={'product'}
                                  coverageAmount={product.coverageAmount}
                                  currency={ComprehensivePricingCurrency.CAD}
                                  isFirstInList={index === 0}
                                  isJointProduct={product.isJointProduct}
                                >
                                  {product.riders.map((rider: ComprehensivePricingRider) => (
                                    <ComprehensivePricingProductCard
                                      language={language}
                                      paymentMode={paymentMode}
                                      key={rider.productCode}
                                      displayName={rider.displayName?.[language] || rider.productCode}
                                      cost={paymentModeToModeToCost(
                                        paymentMode,
                                        {
                                          monthlyCost: rider.monthlyCost,
                                          annualCost: rider.annualCost,
                                          quarterlyCost: rider.quarterlyCost,
                                          semiAnnualCost: rider.semiAnnualCost,
                                          singlePremiumCost: rider.singlePremiumCost,
                                        },
                                        ComprehensivePricingCurrency.CAD,
                                      )}
                                      type={'rider'}
                                      coverageAmount={rider.coverageAmount}
                                      currency={ComprehensivePricingCurrency.CAD}
                                      isFirstInList={false}
                                    />
                                  ))}
                                </ComprehensivePricingProductCard>
                              ))}
                              {party.riders.map((rider: ComprehensivePricingRider, index) => (
                                <ComprehensivePricingProductCard
                                  language={language}
                                  paymentMode={paymentMode}
                                  key={rider.productCode}
                                  displayName={rider.displayName?.[language] || rider.productCode}
                                  cost={paymentModeToModeToCost(
                                    paymentMode,
                                    {
                                      monthlyCost: rider.monthlyCost,
                                      annualCost: rider.annualCost,
                                      quarterlyCost: rider.quarterlyCost,
                                      semiAnnualCost: rider.semiAnnualCost,
                                      singlePremiumCost: rider.singlePremiumCost,
                                    },
                                    ComprehensivePricingCurrency.CAD,
                                  )}
                                  type={'product'}
                                  coverageAmount={rider.coverageAmount}
                                  currency={ComprehensivePricingCurrency.CAD}
                                  isFirstInList={index === 0 && !party.products.length}
                                />
                              ))}
                            </div>
                          )}
                        </Box>
                      );
                    })}
                    {showContractSection && (
                      <Box mb={1}>
                        <Box mb={0.5}>
                          <PdfSubsectionHeader>{translate('comprehensivePricing.contract')}</PdfSubsectionHeader>
                        </Box>
                        <Box>
                          {contract.riders.map((rider, index) => (
                            <ComprehensivePricingProductCard
                              key={rider.productCode}
                              language={language}
                              paymentMode={paymentMode}
                              displayName={rider.displayName?.[language] || rider.productCode}
                              coverageAmount={rider.coverageAmount}
                              cost={paymentModeToModeToCost(
                                paymentMode,
                                {
                                  monthlyCost: rider.monthlyCost,
                                  annualCost: rider.annualCost,
                                  quarterlyCost: rider.quarterlyCost,
                                  semiAnnualCost: rider.semiAnnualCost,
                                  singlePremiumCost: rider.singlePremiumCost,
                                },
                                ComprehensivePricingCurrency.CAD,
                              )}
                              type={'product'}
                              currency={ComprehensivePricingCurrency.CAD}
                              isFirstInList={index === 0}
                            />
                          ))}
                          {contract.fees && !contract.fees.hideFees && (
                            <ComprehensivePricingProductCard
                              key={'fees'}
                              language={language}
                              paymentMode={paymentMode}
                              displayName={translate('comprehensivePricing.policyFees')}
                              cost={paymentModeToModeToCost(
                                paymentMode,
                                {
                                  monthlyCost: contract.fees.monthlyCost,
                                  annualCost: contract.fees.annualCost,
                                  quarterlyCost: contract.fees.quarterlyCost,
                                  semiAnnualCost: contract.fees.semiAnnualCost,
                                  singlePremiumCost: contract.fees.singlePremiumCost,
                                },
                                ComprehensivePricingCurrency.CAD,
                              )}
                              type={'product'}
                              currency={ComprehensivePricingCurrency.CAD}
                              isFirstInList={contract.riders.length === 0}
                              tooltipMessage={contract.fees.informationMessage}
                            />
                          )}
                          {contract.equivalentSingleAge && (
                            <ComprehensivePricingProductCard
                              displayName={translate('comprehensivePricing.equivalentSingleAge.title')}
                              language={language}
                              paymentMode={paymentMode}
                              type='esa'
                              equivalentSingleAge={contract.equivalentSingleAge}
                              isFirstInList={contract.riders.length === 0 && contract.fees && !contract.fees.hideFees}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {comprehensivePricing.contracts.length > 1 && (
                      <Box>
                        <Total
                          language={language}
                          type='subtotal'
                          displayName={translate('comprehensivePricing.subtotal')}
                          paymentMode={paymentMode}
                          cost={paymentModeToModeToCost(
                            paymentMode,
                            {
                              monthlyCost: contract.monthlyCost,
                              annualCost: contract.annualCost,
                              quarterlyCost: contract.quarterlyCost,
                              semiAnnualCost: contract.semiAnnualCost,
                              singlePremiumCost: contract.singlePremiumCost,
                            },
                            ComprehensivePricingCurrency.CAD,
                          )}
                          currency={ComprehensivePricingCurrency.CAD}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
            <Box ml={1}>
              <Total
                type='total'
                language={language}
                displayName={translate('comprehensivePricing.total')}
                paymentMode={paymentMode}
                cost={paymentModeToModeToCost(
                  paymentMode,
                  {
                    monthlyCost: comprehensivePricing.monthlyCost,
                    annualCost: comprehensivePricing.annualCost,
                    quarterlyCost: comprehensivePricing.quarterlyCost,
                    semiAnnualCost: comprehensivePricing.semiAnnualCost,
                    singlePremiumCost: comprehensivePricing.singlePremiumCost,
                  },
                  ComprehensivePricingCurrency.CAD,
                )}
                currency={ComprehensivePricingCurrency.CAD}
              />
            </Box>
          </ProductContainer>
        </Grid>
      </Grid>
    </Box>
  );
}
